import { PencilSquareIcon } from '@heroicons/react/20/solid'
import { useState } from 'react'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'

import Banner from '@/components/TailwindUI/Banner'
import Skeleton from '@/components/TailwindUI/Skeleton'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import AudienceSimpleRule from '@/pages/SpaceAppBuilder/components/AudienceSimpleRule/AudienceSimpleRule'
import { App, Feature, TriggerTypes } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { getFetchAppQueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'

import AppAudienceModal from './AppAudienceForm'

type Props = {
  readOnly?: boolean
  onEditApplication: (values: any) => void
  isSavingAudience: boolean
}

const none: Feature = {
  id: '123',
  name: 'None'
}

function filterAudienceTargetingFeatures(features: Feature[] | undefined) {
  return (
    features?.filter(
      (feature: Feature) => feature.type === TriggerTypes.microappView || feature.type === TriggerTypes.knowledge
    ) || []
  )
}

export default function AppAudienceTargeting({ readOnly = true, onEditApplication, isSavingAudience }: Props) {
  const { spaceId, appId } = useParams<{ spaceId: string; appId: string }>()
  const [isEditingAudience, setIsEditingAudience] = useState(false)

  const appQuery = useQuery<App>({
    ...getFetchAppQueryOptions({ spaceId, appId })
  })

  const listItems = [none, ...filterAudienceTargetingFeatures(appQuery.data?.features)]
  const isEmpty = listItems?.length <= 1
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]
  return isEmpty ? null : (
    <div className="mt-6 w-full bg-white p-4">
      <div className="h-10 sm:flex sm:items-center">
        <h1 className="text-lg font-semibold leading-6 text-gray-900">Audience Targeting</h1>
        {!readOnly && (
          <button
            type="button"
            className="ml-auto flex items-center space-x-0.5 px-0 py-1.5 pl-0 text-center text-sm font-semibold leading-6 text-blue-600 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            onClick={() => setIsEditingAudience(true)}
          >
            {' '}
            <PencilSquareIcon className="h-4 w-4 hover:cursor-pointer" />
            <span className="whitespace-nowrap">Edit Audience</span>
          </button>
        )}
      </div>
      <hr className="mt-2" />
      {appQuery.data?.audience && appQuery.data?.audience?.length > 0 ? (
        <div>
          {}
          <label className="mt-2 block truncate text-sm text-gray-500">
            Applies to {agentsFlagOn ? 'Agent' : 'App'} UI and Knowledge triggers only.
          </label>
          {isSavingAudience ? (
            <li data-testid="loader" className="pointer-events-none flex items-center justify-between p-4 pl-0">
              <Skeleton className="h-4 w-full pr-8" />
            </li>
          ) : (
            <AudienceSimpleRule audiences={appQuery.data.audience} />
          )}
        </div>
      ) : (
        <div className="mt-2">
          <Banner variant="warning">
            <div>
              <span>
                No Audience has been provided, features with an {agentsFlagOn ? 'Agent' : 'App'} UI or a Knowledge
                trigger will not be visible.
              </span>
              {!readOnly && (
                <button
                  type="button"
                  className="block px-0 py-1.5 pb-0 pl-0 text-center text-sm font-semibold leading-6 text-blue-600 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                  onClick={() => setIsEditingAudience(true)}
                >
                  <span className="whitespace-nowrap">Edit Audience</span>
                </button>
              )}
            </div>
          </Banner>
        </div>
      )}
      {appQuery.data && (
        <AppAudienceModal
          show={isEditingAudience}
          onClose={() => setIsEditingAudience(false)}
          onSubmit={(values: any) => {
            onEditApplication(values)
            setIsEditingAudience(false)
          }}
          values={appQuery.data}
        />
      )}
    </div>
  )
}
